// The cookie banner (first visit, or after the categories changed) and the cookie settings
// dialog (footer link). "Only necessary" is as easy as "Accept all"; nothing optional is stored
// before a choice, and the page stays usable while the banner is shown.
import { useEffect, useState } from "react";
import { Link } from "../lib/router";
import { useConsent } from "./consent";
import { Modal } from "./Modal";

export function CookieBanner() {
  const { choice, open, decide, openSettings, closeSettings } = useConsent();
  if (open) return <CookieSettings onClose={closeSettings} onSave={decide} />;
  if (choice) return null;
  return (
    <section className="cookie-banner" role="region" aria-label="Cookie settings" data-testid="cookie-banner">
      <div className="cookie-text">
        <strong>Cookies &amp; storage</strong>
        <p>
          We use one cookie to keep you signed in and store draft backups in your browser — both are needed for the site
          to work. With your OK we also remember your display settings on this device. No tracking, no ads.{" "}
          <Link href="/legal/cookies">Cookie notice</Link>
        </p>
      </div>
      <div className="cookie-actions">
        <button
          type="button"
          className="btn"
          onClick={() => decide({ preferences: false, statistics: false })}
          data-testid="cookie-necessary"
        >
          Only necessary
        </button>
        <button type="button" className="btn" onClick={openSettings} data-testid="cookie-settings">
          Settings
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => decide({ preferences: true, statistics: true })}
          data-testid="cookie-accept"
        >
          Accept all
        </button>
      </div>
    </section>
  );
}

function CookieSettings({
  onClose,
  onSave,
}: {
  onClose: () => void;
  onSave: (c: { preferences: boolean; statistics: boolean }) => void;
}) {
  const current = useConsent((s) => s.choice);
  const [preferences, setPreferences] = useState(current?.preferences ?? false);
  const [statistics, setStatistics] = useState(current?.statistics ?? false);
  useEffect(() => {
    setPreferences(current?.preferences ?? false);
    setStatistics(current?.statistics ?? false);
  }, [current]);
  const Row = ({
    title,
    text,
    checked,
    onChange,
    locked,
    testId,
  }: {
    title: string;
    text: string;
    checked: boolean;
    onChange?: (v: boolean) => void;
    locked?: boolean;
    testId: string;
  }) => (
    <label className={`cookie-row${locked ? " locked" : ""}`}>
      <input
        type="checkbox"
        checked={checked}
        disabled={locked}
        onChange={(e) => onChange?.(e.target.checked)}
        data-testid={testId}
      />
      <span>
        <strong>{title}</strong>
        <span className="muted small">{text}</span>
      </span>
    </label>
  );
  return (
    <Modal title="Cookie settings" onClose={onClose}>
      <div className="stack" data-testid="cookie-dialog">
        <Row
          title="Strictly necessary (always on)"
          text="The sign-in cookie (eb_session), draft backups of songs you're writing (IndexedDB) and this choice (eb.consent)."
          checked
          locked
          testId="cookie-cat-necessary"
        />
        <Row
          title="Preferences"
          text="Remember your look (Studio Feel, Hifi Feel, Zoo or Eigenbloom, and its shape) and which guided tours you have seen on this device (eb.settings, eb.tour). Without it they last for this visit."
          checked={preferences}
          onChange={setPreferences}
          testId="cookie-cat-preferences"
        />
        <Row
          title="Statistics"
          text="Not used at the moment. If we ever add anonymous usage statistics, they will only run with this switched on."
          checked={statistics}
          onChange={setStatistics}
          testId="cookie-cat-statistics"
        />
        <p className="muted small">
          You can change this at any time from “Cookie settings” at the bottom of every page. Details:{" "}
          <Link href="/legal/cookies">Cookie notice</Link> · <Link href="/legal/privacy">Privacy policy</Link>
        </p>
        <div className="row">
          <button
            type="button"
            className="btn"
            onClick={() => onSave({ preferences: false, statistics: false })}
            data-testid="cookie-dialog-necessary"
          >
            Only necessary
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => onSave({ preferences, statistics })}
            data-testid="cookie-save"
          >
            Save choice
          </button>
        </div>
      </div>
    </Modal>
  );
}
